import React from 'react'
import { 
  ShieldCheckIcon, 
  HandRaisedIcon,
  EyeIcon,
  DocumentCheckIcon,
  UserGroupIcon,
  LockClosedIcon,
  AcademicCapIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'

const CodeOfConductPage: React.FC = () => {
  const codePoints = [
    {
      icon: HandRaisedIcon,
      title: '1. Honesty and Good Faith',
      description: 'We act with honesty and in good faith, placing the interests of investors above all else.'
    },
    {
      icon: EyeIcon,
      title: '2. Diligence',
      description: 'All research reports and recommendations are prepared with due skill, care, and professional judgment, following a thorough analysis of information and data.'
    },
    {
      icon: ShieldCheckIcon,
      title: '3. Conflict of Interest',
      description: 'Any potential or actual conflicts of interest are fully disclosed. Our independence in research and recommendations is never compromised for personal or third-party gain.'
    },
    {
      icon: DocumentCheckIcon,
      title: '4. Insider Trading and Front Running',
      description: 'We do not engage in insider trading, front running, or misuse of unpublished price-sensitive information.'
    },
    {
      icon: LockClosedIcon,
      title: '5. Confidentiality',
      description: 'All client information and unpublished research material are treated with the highest level of confidentiality and are never misused for personal advantage.'
    },
    {
      icon: AcademicCapIcon,
      title: '6. Professional Standards',
      description: 'We uphold the highest standards of professionalism. We avoid untrue statements, misleading claims, or suppression of material facts in research reports and public communications.'
    },
    {
      icon: UserGroupIcon,
      title: '7. Regulatory Compliance',
      description: 'We comply with all SEBI regulations, circulars, and guidelines applicable to Research Analysts, ensuring that our services remain transparent, ethical, and legally sound.'
    },
    {
      icon: BuildingOfficeIcon,
      title: '8. Senior Management Responsibility',
      description: 'Where applicable, senior management carries ultimate responsibility for ensuring compliance with this Code of Conduct and fostering a culture of integrity across the organization.'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ShieldCheckIcon aria-hidden="true" focusable="false" className="w-16 h-16 mx-auto mb-6 text-blue-200" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Code of Conduct
            </h1>
            <p className="text-2xl md:text-3xl font-semibold mb-4 text-blue-100">
              Analyzerview Market Research
            </p>
            <p className="text-lg md:text-xl mb-8 text-blue-100 max-w-4xl mx-auto">
              As per Regulation 24 read with Schedule III of the SEBI (Research Analysts) Regulations, 2014
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-lg text-gray-300 leading-relaxed">
              At Analyzerview, we strictly adhere to the Code of Conduct prescribed by SEBI for Research Analysts. 
              This framework ensures that all research activities are carried out with integrity, independence, and 
              accountability, while safeguarding the interests of investors.
            </p>
          </div>
        </div>
      </section>

      {/* Code Points Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="sr-only">Code of Conduct Principles</h2>
          <ul className="grid gap-8 lg:grid-cols-2 list-none">
            {codePoints.map((point, index) => (
              <li key={index} className="h-full">
                <Card className="h-full">
                  <div className="flex flex-col h-full">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-12 h-12 bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <point.icon aria-hidden="true" focusable="false" className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white mb-3">
                          {point.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-300 leading-relaxed flex-1">
                      {point.description}
                    </p>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SEBI Compliance Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <div className="text-center">
              <ShieldCheckIcon aria-hidden="true" focusable="false" className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-6">
                SEBI Registered & Compliant
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                We are committed to maintaining the highest standards of professional conduct in accordance 
                with SEBI regulations. Our adherence to this Code of Conduct ensures that our research services 
                are delivered with transparency, integrity, and in the best interests of our clients.
              </p>
              <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border border-blue-700 rounded-lg p-6">
                <p className="text-sm text-blue-300 font-medium">
                  SEBI Registered Research Analyst
                </p>
                <p className="text-lg font-bold text-blue-200">
                  Registration No: INH000023719
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Questions or Concerns?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            If you have any questions regarding our Code of Conduct or wish to report any concerns, 
            please do not hesitate to contact us.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-900"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  )
}

export default CodeOfConductPage